// src/components/states/StateFormModal.js
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Label,
  TextInput,
  Select,
  Textarea,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "flowbite-react";

const REGIME_OPTIONS = ["TAX_LIEN", "TAX_DEED", "REDEEMABLE_DEED", "MIXED"];

function toNumberOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function StateFormModal({
  open,
  onClose,
  initialData, // null/undefined => create | object => edit
  saving,
  onSubmit,
}) {
  const isEdit = !!initialData?.id;

  const defaults = useMemo(
    () => ({
      code: "",
      name: "",
      default_regime_type: "TAX_DEED",
      default_penalty_rate: "",
      default_interest_rate: "",
      default_redemption_period_days: "",
      notes: "",
    }),
    [],
  );

  const [form, setForm] = useState(defaults);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    if (!initialData) {
      setForm(defaults);
      setTouched({});
      return;
    }

    setForm({
      code: initialData.code ?? "",
      name: initialData.name ?? "",
      default_regime_type: initialData.default_regime_type ?? "TAX_DEED",
      default_penalty_rate:
        (initialData.default_penalty_rate ??
        initialData.default_penalty_rate === 0)
          ? String(initialData.default_penalty_rate)
          : "",
      default_interest_rate:
        (initialData.default_interest_rate ??
        initialData.default_interest_rate === 0)
          ? String(initialData.default_interest_rate)
          : "",
      default_redemption_period_days:
        (initialData.default_redemption_period_days ??
        initialData.default_redemption_period_days === 0)
          ? String(initialData.default_redemption_period_days)
          : "",
      notes: initialData.notes ?? "",
    });
    setTouched({});
  }, [open, initialData, defaults]);

  const errors = {
    code: !form.code?.trim() ? "Obrigatório" : null,
    name: !form.name?.trim() ? "Obrigatório" : null,
    default_regime_type: !form.default_regime_type ? "Obrigatório" : null,
  };

  const hasErrors = !!(
    errors.code ||
    errors.name ||
    errors.default_regime_type
  );

  function setField(key, value) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function markTouched(key) {
    setTouched((t) => ({ ...t, [key]: true }));
  }

  function handleSave() {
    setTouched({ code: true, name: true, default_regime_type: true });
    if (hasErrors) return;

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      default_regime_type: form.default_regime_type,
      default_penalty_rate: toNumberOrNull(form.default_penalty_rate),
      default_interest_rate: toNumberOrNull(form.default_interest_rate),
      default_redemption_period_days: toNumberOrNull(
        form.default_redemption_period_days,
      ),
      notes: form.notes?.trim() ? form.notes.trim() : null,
    };

    onSubmit(payload);
  }

  return (
    <Modal show={open} onClose={onClose}>
      <ModalHeader>{isEdit ? "Editar State" : "Novo State"}</ModalHeader>

      <ModalBody>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="code" value="Code (ex: FL)" />
              <TextInput
                id="code"
                value={form.code}
                onChange={(e) => setField("code", e.target.value)}
                onBlur={() => markTouched("code")}
                placeholder="FL"
                color={touched.code && errors.code ? "failure" : "gray"}
                helperText={touched.code && errors.code ? errors.code : null}
                disabled={!!saving}
              />
            </div>

            <div>
              <Label htmlFor="name" value="Name" />
              <TextInput
                id="name"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                onBlur={() => markTouched("name")}
                placeholder="Florida"
                color={touched.name && errors.name ? "failure" : "gray"}
                helperText={touched.name && errors.name ? errors.name : null}
                disabled={!!saving}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="default_regime_type" value="Default Regime Type" />
            <Select
              id="default_regime_type"
              value={form.default_regime_type}
              onChange={(e) => setField("default_regime_type", e.target.value)}
              onBlur={() => markTouched("default_regime_type")}
              disabled={!!saving}
            >
              {REGIME_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
            {touched.default_regime_type && errors.default_regime_type ? (
              <p className="mt-1 text-sm text-red-600">
                {errors.default_regime_type}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label
                htmlFor="default_penalty_rate"
                value="Default Penalty Rate"
              />
              <TextInput
                id="default_penalty_rate"
                type="number"
                step="0.01"
                value={form.default_penalty_rate}
                onChange={(e) =>
                  setField("default_penalty_rate", e.target.value)
                }
                placeholder="Penalty Rate"
                disabled={!!saving}
              />
            </div>

            <div>
              <Label
                htmlFor="default_interest_rate"
                value="Default Interest Rate"
              />
              <TextInput
                id="default_interest_rate"
                type="number"
                step="0.01"
                value={form.default_interest_rate}
                onChange={(e) =>
                  setField("default_interest_rate", e.target.value)
                }
                placeholder="Interest Rate"
                disabled={!!saving}
              />
            </div>

            <div>
              <Label
                htmlFor="default_redemption_period_days"
                value="Default Redemption Period (days)"
              />
              <TextInput
                id="default_redemption_period_days"
                type="number"
                step="1"
                value={form.default_redemption_period_days}
                onChange={(e) =>
                  setField("default_redemption_period_days", e.target.value)
                }
                placeholder="Default Redemption Period (days)"
                disabled={!!saving}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes" value="Notes" />
            <Textarea
              id="notes"
              rows={4}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Observações gerais do estado..."
              disabled={!!saving}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button className="text-slate-600" onClick={handleSave} disabled={!!saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
        <Button color="gray" onClick={onClose} disabled={!!saving}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
